import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import EmptyState from '../../../../assets/img/Errors/empty.png';
import DateRangePicker from '../../../../components/date-range-picker/date-range-picker';
import { shorten } from '../../dashboard-shared/dashboard-text-utils';
import {
    filterDynamicDataById,
    formatDashboardDynamicFields,
} from '../dashboard-dynamic-fields-util';
import { getLastUpdatedDateColor,
        sumHour } from "../../../new-dashboard/dashboard-util/dashboard-utils";
import DashboardDynamicFieldsWidgetHeader from '../dynamic-fields-widget/dashboard-dynamic-fields-widget-header';
import { useWidgetState } from '../dynamic-fields-widget/useWidgetState';
import DashboardDynamicFieldsTable from './dashboard-dynamic-fields-table';
import DynamicFieldBulk from '../../../../components/DynamicFieldBulk/DynamicFieldBulk';
import DynamicFieldsAvatarGroup from "../dynamic-fields-table/DynamicFieldsAvatarGroup";

const DashboardDynamicFieldsTableBody = ({
    isModal,
    rows,
    rowsRolledOver,
    enableRowModal,
    dynamicFieldsForRowModal,
    rowIds,
    isAdmin,
    isStaffer,
    ProjectTypeId,
    TaskTypeId,
    fetchData,
}) => {
    const {
        showModal,
        showButtons,
        sortBy,
        handleShowModal,
        handleHideModal,
        handleSelectSortBy,
        customDates,
        showCalendar,
        handleHideCalendar,
        handleSetStartDate,
        handleSetEndDate,
    } = useWidgetState();

    const [selectedId, setSelectedId] = useState(rowIds?.[0] ?? -1);
    const [modalTitle, setModalTitle] = useState('');
    const [modalRows, setModalRows] = useState([]);
    const [modalRowsRolledOver, setModalRowsRolledOver] = useState([]);
    const [modalColumns, setModalColumns] = useState([]);

    useEffect(() => {
        if (customDates.startDate && customDates.endDate) {
            handleHideCalendar();
        }
    }, [customDates]);

    const handleUpdateColumnsAndRows = ({
        updatedSortType,
        updatedCustomDates,
        updatedSelectedId,
        updatedRowIndex
    }) => {
        // updated parameters used to force a rerender on row modal
        const { columns: newColumns, rows: newRows, rowsRolledOver: newRowsRolledOver } = formatDashboardDynamicFields({
            sortType: updatedSortType ?? sortBy,
            dynamicFields: {
                ...dynamicFieldsForRowModal,
                data: filterDynamicDataById({
                    idField: dynamicFieldsForRowModal.idField,
                    id: updatedSelectedId ?? selectedId,
                    data: dynamicFieldsForRowModal.data,
                }),
            },
            customDates: updatedCustomDates ?? customDates,
        });

        setModalRows(newRows);
        setModalRowsRolledOver(newRowsRolledOver)
        setModalColumns(newColumns);
    };

    const handleSelectSortByAndUpdateProps = (selectedValue) => {
        handleSelectSortBy(selectedValue);
        handleUpdateColumnsAndRows({ updatedSortType: selectedValue });
    };

    const handleSetStartDateAndUpdateProps = (startDate) => {
        handleSetStartDate(startDate);
        handleUpdateColumnsAndRows({ updatedCustomDates: { ...customDates, startDate } });
    };

    const handleSetEndDateAndUpdateProps = (endDate) => {
        handleSetEndDate(endDate);
        handleUpdateColumnsAndRows({ updatedCustomDates: { ...customDates, endDate } });
    };

    const headerProps = {
        isModal: true,
        showButtons,
        sortBy,
        handleShowModal,
        handleHideModal,
        handleSelectSortBy: handleSelectSortByAndUpdateProps,
        customDates,
        title: modalTitle,
    };

    const tableProps = {
        isModal: true,
        isAdmin: isAdmin,
        isStaffer: isStaffer,
        enableRowModal: false,
        rows: modalRows,
        rowsRolledOver: modalRowsRolledOver,
        columns: modalColumns
    };

    if (rows.length === 0) {
        return (
            <div className="d-flex align-items-center justify-content-center h-100">
                <img src={EmptyState} alt="No Dynamic Fields To Display" />
            </div>
        );
    }

    let initialData = rows.map(r => r);

    if (isModal) {
        let lenghtArray = initialData[0].length;
        let newRow = ['Total'];

            for (let i = 0; i < lenghtArray - 1; i++) {
                newRow.push("-");
            }

            for(let i = 0; i < initialData.length; i++){
                let completeRow = initialData[i];
                for (let j = 0; j < completeRow.length -1 ; j++){
                    if (j > 0) {
                        let partial = newRow[j];
                        let data = completeRow[j];
                        newRow[j] = sumHour(partial, data, true);
                    }
                }
            }

            initialData.push(newRow)
    }

    const getAvatarGroupContent = (row, data) => {
        if (row[0] === 'Total') return "-";
        let nameWithTypes = row.slice(5, -1)
            .filter(item => item !== '-' && item !== 'N/A' && item !== '')
            .flatMap((nameString, index) => {
                const types = ['teal', 'purple', 'magenta']; // Define your types array
                const names = nameString.split(',').map(name => name.trim()); // Split by comma and trim whitespace
                return names.map(name => ({
                    Name: name,
                    Type: types[index]
                }));
            });
        return nameWithTypes.length > 0 ? <DynamicFieldsAvatarGroup members={nameWithTypes}/> : "-";
    };

    const getCellContent = (data, shortenedData, cellIdx) => {
        if (((isAdmin || isStaffer) && cellIdx === 8) || (!isAdmin && !isStaffer && cellIdx === 5)) {
            return { content: shortenedData, style: { color: getLastUpdatedDateColor(shortenedData) } };
        }
        return { content: shortenedData, style: {} };
    };

    const renderTableCell = (data, cellIdx, row, isOldDataClass) => {
        if (isAdmin || isStaffer) {
            if (cellIdx === 5) {
                if (!isModal) return null;
                const content = getAvatarGroupContent(row, data);
                return <td key={cellIdx} className={`${isOldDataClass}modal-project-by-col-1 p-0`}>{content}</td>;
            }
            if(cellIdx === 6 || cellIdx === 7) {
                return null;
            }
        }

        const shortenedData = shorten(data, isModal ? 30 : 33);
        const cellData = shortenedData === data ? data : (
            <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>{data}</Tooltip>}
            >
                <p>{shortenedData}</p>
            </OverlayTrigger>
        );

        const { content, style } = getCellContent(cellData, shortenedData, cellIdx);
        const cellClassName = `${isModal ? isOldDataClass + 'modal' : isOldDataClass + 'normal'}-project-by-col-1 p-0`;

        return (
            <td key={cellIdx} style={style} className={cellClassName}>
                {content}
            </td>
        );
    };

    return (
        <>
            <tbody className="w-100 h-100">
                {initialData.map((row, rowIdx) => (
                    <tr
                        key={rowIdx}
                        className={`${
                            isModal ? 'modal-row' + (row[0] === 'Total' ? ' totalRow' :'') : ''
                        } project-table-row-full project-by-padding ${
                            enableRowModal ? 'dynamic-fields-main-row' : ''
                        }`}
                        onClick={() => {
                            if (!enableRowModal) return;
                            setSelectedId(rowIds[rowIdx]);
                            setModalTitle(row[0]);
                            handleUpdateColumnsAndRows({ updatedSelectedId: rowIds[rowIdx], updatedRowIndex: rowIdx });
                            if (!isModal) handleShowModal();
                        }}
                    >
                        {row.map((data, cellIdx) => {
                            let isOldDataRow = rowsRolledOver ? rowsRolledOver[rowIdx] : rowsRolledOver;
                            let isOldData = isOldDataRow ? isOldDataRow[cellIdx] : false;
                            let oldDataClass = isOldData ? "isOldData " : "";

                            return renderTableCell(data, cellIdx, row, oldDataClass);
                        })}
                    </tr>
                ))}
            </tbody>
            {   enableRowModal && (
                <>
                    <Modal
                        show={showModal}
                        onHide={handleHideModal}
                        contentClassName="modal-styles-dashboard w-100"
                        dialogClassName="projects-by-popup-dialog"
                    >
                        <Modal.Header className="p-0 w-100">
                            <DashboardDynamicFieldsWidgetHeader {...headerProps} />
                        </Modal.Header>
                        {   (isAdmin || isStaffer) &&
                            <Modal.Body className="p-0">
                                <DashboardDynamicFieldsTable {...tableProps} />
                            </Modal.Body>
                        }
                        {   (!isAdmin && !isStaffer)  &&
                            <DynamicFieldBulk
                                objectType={"TaskComment"}
                                objectId={0}
                                taskTypeId={TaskTypeId}
                                projectTypeId={ProjectTypeId}
                                columnNamesNotNeeded={["Description"]}
                                hasProjectColumn={true}
                                hasMemberColumn={false}
                                hasDueDate={false}
                                fetchData={fetchData}
                            />
                        }
                    </Modal>
                    <Modal
                        show={showCalendar}
                        onHide={handleHideCalendar}
                        className="calendar-modal"
                        contentClassName="calendar-modal-content"
                        backdropClassName="calendar-modal-backdrop"
                        dialogClassName="completed-vs-overdue-modal-dialog"
                    >
                        <DateRangePicker
                            start={customDates.startDate}
                            setStart={handleSetStartDateAndUpdateProps}
                            end={customDates.endDate}
                            setEnd={handleSetEndDateAndUpdateProps}
                        />
                    </Modal>
                </>
            )}
        </>
    );
};

DashboardDynamicFieldsTableBody.propTypes = {
    isModal: PropTypes.bool,
    rows: PropTypes.array,
    rowsRolledOver: PropTypes.array,
    enableRowModal: PropTypes.bool,
    rowIds: PropTypes.array,
    isAdmin: PropTypes.bool,
    isStaffer: PropTypes.bool,
    ProjectTypeId: PropTypes.number,
    TaskTypeId: PropTypes.number,
    fetchData: PropTypes.func,
};

export default DashboardDynamicFieldsTableBody;
